import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';

const METRIC_ORDER = [
  'documents_missing_file',
  'payslips_available',
  'payslips_draft',
  'payslips_signed',
  'payslips_observed',
  'documents_pending',
  'documents_expired',
  'pending_leaves',
  'pending_expense_reports',
  'pending_expense_cents',
  'late_arrivals_week',
  'awards_count',
  'compliance_rate',
];

function riskVariant(level) {
  if (level === 'high') return 'error';
  if (level === 'medium') return 'pending';
  return 'approved';
}

export function PeopleIntelligence() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    api
      .get('/api/people-intelligence/summary')
      .then((r) => {
        setData(r.data);
        setLoadErr(false);
      })
      .catch(() => {
        setData(null);
        setLoadErr(true);
      });
  }, []);

  const score = data?.score ?? 0;
  const risk = data?.risk_level ?? 'low';
  const insights = Array.isArray(data?.insights) ? data.insights : [];
  const recs = Array.isArray(data?.recommendations) ? data.recommendations : [];

  const metricCards = useMemo(() => {
    const metrics = data?.metrics && typeof data.metrics === 'object' ? data.metrics : {};
    return METRIC_ORDER.filter((k) => metrics[k] !== undefined).map((k) => ({
      key: k,
      label: t(`pi.metric_${k}`),
      value: String(metrics[k]),
    }));
  }, [data, t]);

  const features = [1, 2, 3, 4]
    .map((i) => t(`comingSoon.peopleIntelligence.f${i}`))
    .filter((x) => typeof x === 'string' && x.length > 0);

  return (
    <div className="page-stack pi-page pi-page--v17">
      <header className="pi-hero panel panel--elevated">
        <div>
          <h1 className="pi-hero__title">{t('pi.title')}</h1>
          <p className="pi-hero__lead">{t('pi.subtitle')}</p>
          <p className="panel__muted text-sm">{t('pi.rcLine')}</p>
          <p className="panel__muted text-sm">{t('pi.privacyFinances')}</p>
        </div>
        <div className="pi-hero__score-ring" style={{ '--pi-score': Math.min(100, Math.max(0, score)) }}>
          <div className="pi-hero__score-inner">
            <span className="pi-hero__score-value">{score}</span>
            <span className="pi-hero__score-max">/100</span>
          </div>
        </div>
      </header>

      <div className="banner banner--info pi-rules-banner">
        <strong>{t('pi.rulesTitle')}</strong> — {t('pi.rulesBanner')}
      </div>
      <div className="banner banner--muted pi-next-ia-banner">{t('pi.flowIaBanner')}</div>
      <p className="panel__muted text-sm pi-commercial-hint">{t('pi.commercialHint')}</p>

      <section className="pi-exec-brief panel panel--elevated" id="pi-exec-brief" aria-labelledby="pi-exec-brief-title">
        <div className="pi-exec-brief__head">
          <div>
            <h2 id="pi-exec-brief-title" className="panel__title">
              {t('pi.execBriefTitle')}
            </h2>
            <p className="panel__muted text-sm">{t('pi.execBriefLead')}</p>
          </div>
          <div className="pi-exec-brief__score" style={{ '--pi-brief-score': Math.min(100, Math.max(0, score)) }}>
            <span className="pi-exec-brief__score-val">{score}</span>
            <span className="pi-exec-brief__score-max">/100</span>
            <span className="panel__muted text-sm pi-exec-brief__score-lbl">{t('pi.orgHealthLabel')}</span>
          </div>
        </div>
        <p className="panel__muted text-sm pi-exec-brief__hint">{t('pi.orgHealthHint')}</p>

        <h3 className="pi-exec-brief__sub">{t('pi.areaInsightsTitle')}</h3>
        <ul className="pi-exec-brief__areas">
          <li>{t('pi.areaInsight1')}</li>
          <li>{t('pi.areaInsight2')}</li>
          <li>{t('pi.areaInsight3')}</li>
          <li>{t('pi.areaInsight4')}</li>
        </ul>

        <div className="pi-exec-brief__grid">
          <article className="pi-exec-card panel panel--muted">
            <h4 className="pi-exec-card__title">{t('pi.rotRiskTitle')}</h4>
            <p className="pi-exec-card__value">{t('pi.rotRiskValue')}</p>
            <p className="panel__muted text-sm">{t('pi.rotRiskHint')}</p>
          </article>
          <article className="pi-exec-card panel panel--muted">
            <h4 className="pi-exec-card__title">{t('pi.burnoutTitle')}</h4>
            <p className="pi-exec-card__value">{t('pi.burnoutValue')}</p>
            <p className="panel__muted text-sm">{t('pi.burnoutHint')}</p>
          </article>
          <article className="pi-exec-card panel panel--muted">
            <h4 className="pi-exec-card__title">{t('pi.trainingGapTitle')}</h4>
            <p className="panel__muted text-sm">{t('pi.trainingGapBody')}</p>
          </article>
          <article className="pi-exec-card panel panel--muted">
            <h4 className="pi-exec-card__title">{t('pi.climateAlertTitle')}</h4>
            <p className="panel__muted text-sm">{t('pi.climateAlertBody')}</p>
          </article>
        </div>

        <div className="pi-flow-ia-cta-row">
          <button
            type="button"
            className="btn btn--secondary btn--sm btn--flow-ia-cta"
            onClick={() => document.getElementById('flow-ia-pi')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {t('pi.piFlowIaCta')}
          </button>
          <span className="panel__muted text-sm">{t('pi.piFlowIaCtaHint')}</span>
        </div>
      </section>

      {loadErr && <div className="banner banner--info">{t('common.error')}</div>}

      {!loadErr && data && (
        <div className="pi-risk-row">
          <span className="panel__muted">{t('pi.riskLabel')}</span>
          <Badge variant={riskVariant(risk)}>
            {risk === 'high' ? t('pi.riskHigh') : risk === 'medium' ? t('pi.riskMedium') : t('pi.riskLow')}
          </Badge>
        </div>
      )}

      <section className="panel panel--elevated pi-what-now">
        <h2 className="panel__title">{t('pi.whatHrNowTitle')}</h2>
        <p className="panel__muted">{t('pi.whatHrNowIntro')}</p>
        <ul className="pi-card-list pi-what-now__list">
          <li>{t('pi.whatHrNow1')}</li>
          <li>{t('pi.whatHrNow2')}</li>
          <li>{t('pi.whatHrNow3')}</li>
          <li>{t('pi.whatHrNow4')}</li>
          <li>{t('pi.whatHrNow5')}</li>
        </ul>
      </section>

      <section className="panel panel--elevated pi-dimensions-block">
        <h2 className="panel__title">{t('pi.dimensionsTitle')}</h2>
        <ul className="pi-card-list pi-dimensions-list">
          <li>{t('pi.dim_docs')}</li>
          <li>{t('pi.dim_attendance')}</li>
          <li>{t('pi.dim_leaves')}</li>
          <li>{t('pi.dim_expenses')}</li>
          <li>{t('pi.dim_perf')}</li>
          <li>{t('pi.dim_train')}</li>
          <li>{t('pi.dim_climate')}</li>
          <li>{t('pi.dim_culture')}</li>
          <li className="pi-dim-wellbeing">
            <strong className="pi-dim-wellbeing__title">{t('pi.dimWellbeingTitle')}</strong>
            <p className="panel__muted text-sm pi-dim-wellbeing__line">{t('pi.dimWellbeingScoreDemo')}</p>
            <p className="panel__muted text-sm pi-dim-wellbeing__line">{t('pi.dimWellbeingRiskDemo')}</p>
            <p className="panel__muted text-sm pi-dim-wellbeing__line">{t('pi.dimWellbeingPrivacy')}</p>
          </li>
          <li>{t('pi.dim_comms')}</li>
          <li>{t('pi.dim_governance')}</li>
        </ul>
      </section>

      <section className="panel panel--elevated pi-comms-block">
        <h2 className="panel__title">{t('pi.commsSectionTitle')}</h2>
        <div className="grid-2 pi-comms-columns">
          <div>
            <h3 className="pi-lifecycle-recs-title">{t('pi.insightsTitle')}</h3>
            <ul className="pi-card-list">
              <li>{t('pi.commsInsight1')}</li>
              <li>{t('pi.commsInsight2')}</li>
              <li>{t('pi.commsInsight3')}</li>
              <li>{t('pi.commsInsight4')}</li>
            </ul>
          </div>
          <div>
            <h3 className="pi-lifecycle-recs-title">{t('pi.recTitle')}</h3>
            <ul className="pi-card-list">
              <li>{t('pi.commsRec1')}</li>
              <li>{t('pi.commsRec2')}</li>
              <li>{t('pi.commsRec3')}</li>
              <li>{t('pi.commsRec4')}</li>
            </ul>
            <div className="pi-comms-cta-row">
              <Link to="/communications" className="btn btn--sm btn--secondary">
                {t('nav.communications')}
              </Link>
              <Link to="/notifications" className="btn btn--sm btn--secondary">
                {t('nav.notifications')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="panel panel--elevated pi-lifecycle-block">
        <div className="pi-lifecycle-block__head">
          <h2 className="panel__title">{t('pi.lifecycleSectionTitle')}</h2>
          <Link to="/employee-lifecycle" className="btn btn--sm btn--secondary">
            {t('pi.lifecycleCta')}
          </Link>
        </div>
        <div className="pi-lifecycle-metrics">
          <div className="pi-lifecycle-metric">
            <span className="pi-lifecycle-metric__label">{t('pi.lifecycleInsight1')}</span>
            <strong className="pi-lifecycle-metric__value">1</strong>
          </div>
          <div className="pi-lifecycle-metric">
            <span className="pi-lifecycle-metric__label">{t('pi.lifecycleInsight2')}</span>
            <strong className="pi-lifecycle-metric__value">2</strong>
          </div>
          <div className="pi-lifecycle-metric">
            <span className="pi-lifecycle-metric__label">{t('pi.lifecycleInsight3')}</span>
            <strong className="pi-lifecycle-metric__value">2</strong>
          </div>
          <div className="pi-lifecycle-metric">
            <span className="pi-lifecycle-metric__label">{t('pi.lifecycleInsight4')}</span>
            <strong className="pi-lifecycle-metric__value">3</strong>
          </div>
        </div>
        <h3 className="pi-lifecycle-recs-title">{t('pi.recTitle')}</h3>
        <ul className="pi-card-list pi-lifecycle-recs">
          <li>{t('pi.lifecycleRec1')}</li>
          <li>{t('pi.lifecycleRec2')}</li>
          <li>{t('pi.lifecycleRec3')}</li>
        </ul>
      </section>

      <div className="grid-2 pi-risks-opps">
        <section className="panel panel--elevated">
          <h2 className="panel__title">{t('pi.risksBlockTitle')}</h2>
          <ul className="pi-card-list">
            <li>{t('pi.risk_doc')}</li>
            <li>{t('pi.risk_ops')}</li>
            <li>{t('pi.risk_culture')}</li>
            <li>{t('pi.risk_finance')}</li>
            <li>{t('pi.risk_perf')}</li>
            <li>{t('pi.risk_governance')}</li>
          </ul>
        </section>
        <section className="panel panel--elevated pi-insight-card--accent">
          <h2 className="panel__title">{t('pi.oppsTitle')}</h2>
          <ul className="pi-card-list">
            <li>{t('pi.opp_train')}</li>
            <li>{t('pi.opp_awards')}</li>
            <li>{t('pi.opp_wellbeing')}</li>
            <li>{t('pi.opp_onboard')}</li>
            <li>{t('pi.opp_climate')}</li>
            <li>{t('pi.opp_recognition')}</li>
          </ul>
        </section>
      </div>

      {metricCards.length > 0 && (
        <div className="pi-metric-grid">
          {metricCards.map((m) => (
            <div key={m.key} className="pi-metric-card panel panel--elevated">
              <span className="pi-metric-card__label">{m.label}</span>
              <strong className="pi-metric-card__value">{m.value}</strong>
            </div>
          ))}
        </div>
      )}

      <div className="grid-2">
        <section className="panel panel--elevated pi-insight-card">
          <h2 className="panel__title">{t('pi.insightsTitle')}</h2>
          {insights.length === 0 ? (
            <p className="panel__muted">{t('pi.insightsEmpty')}</p>
          ) : (
            <ul className="pi-card-list pi-insight-card__list">
              {insights.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
          <Link to="/records" className="btn btn--sm btn--secondary">
            {t('dashboard.docsStripCta')}
          </Link>
        </section>
        <section className="panel panel--elevated pi-insight-card pi-insight-card--accent">
          <h2 className="panel__title">{t('pi.recTitle')}</h2>
          {recs.length === 0 ? (
            <p className="panel__muted">{t('pi.recEmpty')}</p>
          ) : (
            <ul className="pi-card-list pi-insight-card__list">
              {recs.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="panel panel--elevated pi-rec-actions">
        <h2 className="panel__title">{t('pi.recActionsTitle')}</h2>
        <ul className="pi-card-list">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <li key={i}>{t(`pi.recAction${i}`)}</li>
          ))}
        </ul>
      </section>

      <section className="panel panel--muted pi-footer">
        <h3 className="panel__title">{t('comingSoon.peopleIntelligence.title')}</h3>
        <p className="panel__muted">{t('comingSoon.peopleIntelligence.desc')}</p>
        <ul className="coming-soon-page__features">
          {features.map((f, idx) => (
            <li key={idx}>{f}</li>
          ))}
        </ul>
        <p className="panel__muted text-sm pi-footer-note">{t('pi.roadmapNote')}</p>
      </section>
    </div>
  );
}
